import type { ReactNode } from 'react';

import { ApiRequestError } from '../api/client.js';
import { signIn } from '../auth.js';

export function Loading({ label = 'Loading…' }: { label?: string }) {
  return (
    <div className="flex items-center gap-3 py-8 font-display text-sm uppercase tracking-bug text-field-400">
      <span className="h-4 w-4 animate-spin rounded-full border-2 border-field-700 border-t-brass-400" />
      {label}
    </div>
  );
}

export function EmptyState({ children }: { children: ReactNode }) {
  return (
    <div className="border border-dashed border-field-700 px-5 py-8 text-center text-sm text-field-500">
      {children}
    </div>
  );
}

/**
 * Renders a query failure. A 401 is not really an error here — it just means
 * this section is members-only — so it gets a sign-in prompt instead.
 */
export function ErrorState({ error, action }: { error: unknown; action?: string }) {
  if (error instanceof ApiRequestError && error.isAuthError) {
    return <LoginPrompt>{action ?? 'Log in to see this.'}</LoginPrompt>;
  }

  const message =
    error instanceof Error ? error.message : 'Something went wrong. Try again in a moment.';

  return (
    <div className="border-l-4 border-signal bg-signal/10 px-5 py-4 text-sm text-field-100">
      {message}
    </div>
  );
}

export function LoginPrompt({ children }: { children: ReactNode }) {
  return (
    <div className="flex flex-col items-center gap-3 border border-dashed border-field-700 bg-field-900 px-5 py-8 text-center">
      <p className="font-display text-lg uppercase tracking-tight text-field-200">{children}</p>
      <button type="button" className="btn-primary" onClick={() => signIn()}>
        Log in
      </button>
    </div>
  );
}
